import React from "react"
import PropTypes from "prop-types"
import { NeoButton, NeoBadge, NeoInput, NeoCard, NeoAlert } from "../../../components/Common"

import { useActivityObjectivesTopics } from "../Hooks/useActivityObjectivesTopics"
import { useEvaluationDraft } from "../Hooks/useEvaluationDraft"
import { useSaveEvaluations } from "../Hooks/useSaveEvaluations"
import { useEvaluationMessaging } from "../Hooks/useEvaluationMessaging"
import LevelDropdown from "../../Grade/components/LevelDropdown"

const TechnicalEvaluationForm = ({
    classId,
    idActivity,
    classTeacherId,
    classEmployeeName,
    activityName: activityNameProp,
    evaluationLogic,
}) => {
    const {
        isLoading,
        withLoading,
        formReady,
        levels,
        activeEvent,
        allMembers,
        evaluationMembers,
        defaultLevelId,
        toggleExcludeMember,
        excludedMemberIds
    } = evaluationLogic

    const {
        objectives,
        topics,
        selectedObjective,
        selectedObjectiveId,
        selectedTopicId,
        handleSelectObjective,
        handleSelectTopic,
        handleNext,
        handlePrev,
        isLastTopicOfLastObjective,
        isFirstTopicOfFirstObjective,
        allTopicIds,
        topicMetaById,
    } = useActivityObjectivesTopics({ idActivity, withLoading })

    const {
        draftLevelsByTopicId,
        currentTopicLevels,
        dirtyCount: evaluationDirtyCount,
        handleLevelChange,
    } = useEvaluationDraft({
        idActivity,
        selectedTopicId,
        members: allMembers,
        excludedIds: excludedMemberIds,
        defaultLevelId,
        withLoading,
        activeEventId: activeEvent?.id,
    })

    const { saveAll: saveEvaluations } = useSaveEvaluations({
        idActivity,
        classId,
        members: allMembers,
        excludedIds: excludedMemberIds,
        draftLevelsByTopicId,
        allTopicIds,
        topicMetaById,
        defaultLevelId,
        levels,
        withLoading,
        activeEventId: activeEvent?.id,
        classTeacherId,
        classEmployeeName: classEmployeeName || "",
        activityName: activityNameProp || "",
        eventName: activeEvent?.name || "",
    })

    const { sendEvaluationToMember, sendAllEvaluations, sendingStatus } = useEvaluationMessaging({
        idActivity,
        members: allMembers,
        excludedIds: excludedMemberIds,
        draftLevelsByTopicId,
        allTopicIds,
        topicMetaById,
        levels,
        withLoading,
        activeEventId: activeEvent?.id,
        activityName: activityNameProp || "",
    })

    // Placeholder for Gamification logic if needed in the future
    React.useEffect(() => {
        if (selectedTopicId && topicMetaById[selectedTopicId]) {
            // placeholder for gamification
        }
    }, [selectedTopicId, topicMetaById]);

    // ─── LOADING GATE ───────────────────────────────────────────────
    if (!formReady) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center py-5 animate__animated animate__fadeIn">
                <i className="mdi mdi-loading mdi-spin text-primary font-size-32 mb-3" />
                <p className="text-muted mb-0 font-size-14">Preparando avaliação...</p>
            </div>
        )
    }

    if (!activeEvent) {
        return (
            <NeoAlert
                color="warning"
                title="Período de avaliação fechado"
            >
                Consulte a gerência para iniciar um novo ciclo de avaliações técnicas.
            </NeoAlert>
        )
    }
    // ─── FIM DO LOADING GATE ─────────────────────────────────────────

    return (
        <>
            {/* Seletor de Objetivo e Tópico (Card Fino) */}
            <NeoCard
                thinBorder
                className="mb-4 bg-white animate__animated animate__fadeIn animate__faster"
                bodyClassName="p-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
            >
                <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-5">
                        <label className="form-label fw-bold mb-1 small text-uppercase text-muted">Objetivo</label>
                        <NeoInput
                            type="select"
                            value={selectedObjectiveId}
                            onChange={(e) => handleSelectObjective(e.target.value)}
                            disabled={objectives.length === 0}
                            size="sm"
                        >
                            {objectives.length === 0 ? (
                                <option value="">Nenhum objetivo</option>
                            ) : (
                                objectives.map((o, idx) => (
                                    <option key={o.id} value={String(o.id)}>
                                        {`${idx + 1}. ${o.name || o.title || "Objetivo"}`}
                                    </option>
                                ))
                            )}
                        </NeoInput>
                    </div>

                    <div className="col-12 col-md-6">
                        <label className="form-label fw-bold mb-1 small text-uppercase text-muted">Tópico</label>
                        <NeoInput
                            type="select"
                            value={selectedTopicId}
                            onChange={(e) => handleSelectTopic(e.target.value)}
                            disabled={!selectedObjective || topics.length === 0}
                            size="sm"
                        >
                            {topics.length === 0 ? (
                                <option value="">Nenhum tópico</option>
                            ) : (
                                topics.map((t, idx) => {
                                    const isFund = t.isFundamental ? " [FUNDAMENTAL]" : "";
                                    const xpVal = Number(t.xp) > 0 ? ` (+${t.xp} XP)` : "";
                                    return (
                                        <option key={t.id} value={String(t.id)}>
                                            {`${idx + 1}. ${t.name || t.description || "Tópico"}${isFund}${xpVal}`}
                                        </option>
                                    );
                                })
                            )}
                        </NeoInput>
                    </div>

                    <div className="col-12 col-md-1">
                        <div className="d-flex align-items-center gap-2 justify-content-md-end mb-1">
                            <NeoButton variant="outline-primary" size="sm" onClick={handlePrev} disabled={objectives.length === 0 || isFirstTopicOfFirstObjective} square icon="mdi-chevron-left" />
                            <NeoButton variant="outline-primary" size="sm" onClick={handleNext} disabled={objectives.length === 0 || isLastTopicOfLastObjective} square icon="mdi-chevron-right" />
                        </div>
                    </div>
                </div>
                {isLoading("prefill") && (
                    <div className="mt-2 small text-muted">
                        <i className="mdi mdi-loading mdi-spin me-1" /> Sincronizando dados anteriores...
                    </div>
                )}
            </NeoCard>

            {/* Listagem de Alunos em Card Robustizado */}
            <NeoCard
                thinBorder
                className="bg-white shadow-sm mb-4"
                bodyClassName="p-4"
                title="Alunos para Avaliação"
                subtitle="Atribua o nível de aptidão e envie as avaliações por WhatsApp"
                headerIcon="mdi-account-multiple-outline"
                headerActions={<NeoBadge color="secondary" className="px-3 py-1 font-size-12">{evaluationMembers.length}</NeoBadge>}
                style={{ fontFamily: 'Outfit, sans-serif' }}
            >
                {evaluationMembers.length === 0 ? (
                    <div className="text-center py-5 border rounded bg-light border-dashed">
                        <i className="mdi mdi-account-search-outline fs-1 text-muted opacity-50 mb-3 d-block"></i>
                        <p className="text-muted mb-0 font-size-13">Nenhum aluno apto encontrado.</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {evaluationMembers.map(member => {
                            const sId = String(member.id)
                            const currentLevel = currentTopicLevels?.[sId] || defaultLevelId || ""

                            return (
                                <div key={sId} className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-3 bg-white rounded border shadow-sm mb-2">
                                    <div className="d-flex align-items-center gap-3 w-100 w-md-auto">
                                        <div className="flex-shrink-0">
                                            {member.photo ? (
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    className="rounded-circle border"
                                                    style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="rounded-circle border bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: '48px', height: '48px' }}>
                                                    <i className="mdi mdi-account fs-3" style={{ color: '#9e9e9e' }}></i>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <div className="fw-bold text-dark fs-5">{member.name}</div>
                                                {member.memberNumber && <span className="text-muted small">#{member.memberNumber}</span>}
                                            </div>
                                            <div className="d-flex gap-2 align-items-center mt-1">
                                                {member.status && (
                                                    <NeoBadge color={
                                                        member.status === 'active' ? 'success' :
                                                            member.status === 'suspended' ? 'warning' : 'secondary'
                                                    } className="border px-2 text-capitalize">
                                                        {member.status === 'active' ? 'Ativo' : member.status}
                                                    </NeoBadge>
                                                )}
                                                {member.tag && <span className="badge bg-light text-muted border d-none d-md-inline-block font-size-10">{member.tag}</span>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex flex-column flex-md-row align-items-center gap-2 gap-md-3 w-100 w-md-auto ms-auto">
                                        <div className="w-100 w-md-auto" style={{ minWidth: '160px' }}>
                                            <div className="d-md-none small text-muted text-uppercase fw-bold mb-1 text-center">
                                                Atribuir Nível
                                            </div>
                                            <LevelDropdown
                                                idMember={sId}
                                                currentLevel={currentLevel}
                                                onLevelChange={handleLevelChange}
                                                levels={levels}
                                                disabled={!selectedTopicId || isLoading("prefill") || !activeEvent}
                                                fullWidth={true}
                                                className="w-100 w-md-auto"
                                                toggleClassName="w-100 py-2 py-md-1"
                                            />
                                        </div>

                                        <div className="d-flex align-items-center gap-2 w-100 w-md-auto justify-content-center">
                                            <NeoButton
                                                variant="outline-success"
                                                onClick={() => sendEvaluationToMember(member)}
                                                title="Enviar WhatsApp"
                                                icon="mdi-whatsapp"
                                                className="flex-grow-1 flex-md-grow-0"
                                                size="sm"
                                            >
                                                <span className="ms-1 d-md-none">Enviar</span>
                                            </NeoButton>

                                            <NeoButton
                                                variant="soft-danger"
                                                onClick={() => toggleExcludeMember(sId)}
                                                title="Remover da lista"
                                                icon="mdi-close-circle-outline"
                                                className="flex-grow-1 flex-md-grow-0"
                                                size="sm"
                                            >
                                                <span className="ms-1 d-md-none">Remover</span>
                                            </NeoButton>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                {excludedMemberIds.size > 0 && (
                    <div className="mt-4">
                        <div className="d-flex align-items-center justify-content-between mb-2 px-1">
                            <h6 className="small fw-bold text-muted text-uppercase mb-0">
                                <i className="mdi mdi-eye-off-outline me-1"></i>
                                Alunos Ocultos ({excludedMemberIds.size})
                            </h6>
                            <button
                                type="button"
                                className="btn btn-link p-0 text-decoration-none small text-primary fw-bold animate__animated animate__fadeIn"
                                onClick={() => {
                                    excludedMemberIds.forEach(id => toggleExcludeMember(id))
                                }}
                            >
                                Restaurar Todos
                            </button>
                        </div>

                        <div className="d-flex flex-column gap-2 bg-soft-light p-2 rounded border border-dashed">
                            {allMembers
                                .filter(c => excludedMemberIds.has(String(c.id)))
                                .map(member => (
                                    <div key={member.id} className="d-flex align-items-center justify-content-between p-2 bg-white rounded border-bottom shadow-sm" style={{ opacity: 0.8 }}>
                                        <div className="d-flex align-items-center gap-2">
                                            {member.photo ? (
                                                <img
                                                    src={member.photo}
                                                    alt={member.name}
                                                    className="rounded-circle border"
                                                    style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="rounded-circle border bg-light d-flex align-items-center justify-content-center shadow-sm" style={{ width: '24px', height: '24px' }}>
                                                    <i className="mdi mdi-account" style={{ fontSize: '14px', color: '#9e9e9e' }}></i>
                                                </div>
                                            )}
                                            <span className="small fw-medium text-muted">{member.name}</span>
                                        </div>
                                        <NeoButton
                                            variant="outline-primary"
                                            size="sm"
                                            style={{ height: '24px', fontSize: '10px', padding: '0 8px' }}
                                            onClick={() => toggleExcludeMember(member.id)}
                                        >
                                            Restaurar
                                        </NeoButton>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                <div className="d-flex justify-content-end mt-4 pt-3 border-top gap-2">
                    <NeoButton
                        variant="outline-success"
                        onClick={sendAllEvaluations}
                        disabled={isLoading("saveAll") || objectives.length === 0 || !activeEvent || evaluationMembers.length === 0}
                        icon="mdi-whatsapp"
                        className="px-4 shadow-sm"
                    >
                        Enviar Todos
                    </NeoButton>
                    <NeoButton
                        variant="success"
                        onClick={saveEvaluations}
                        loading={isLoading("saveAll")}
                        disabled={isLoading("saveAll") || objectives.length === 0 || evaluationDirtyCount === 0 || !activeEvent}
                        className="px-4 shadow-sm"
                    >
                        Salvar
                    </NeoButton>
                </div>
            </NeoCard>

            {sendingStatus.loading && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center animate__animated animate__fadeIn"
                    style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, fontFamily: 'Outfit, sans-serif' }}>
                    <div className="bg-white rounded-4 shadow-lg p-4" style={{ maxWidth: '400px', width: '90%' }}>
                        <div className="text-center mb-4">
                            <div className="avatar-md mx-auto mb-3">
                                <span className="avatar-title rounded-circle bg-soft-success text-success font-size-24">
                                    <i className="mdi mdi-whatsapp"></i>
                                </span>
                            </div>
                            <h5 className="font-size-16 fw-bold mb-1 text-dark">Enviando Avaliações</h5>
                            <p className="text-muted mb-0 small">Por favor, aguarde...</p>
                        </div>

                        <div className="mb-3">
                            <div className="d-flex justify-content-between font-size-13 mb-1">
                                <span className="fw-semibold text-muted">{sendingStatus.step}</span>
                                <span className="fw-bold text-success">{sendingStatus.progress}%</span>
                            </div>
                            <div className="progress progress-sm rounded-pill" style={{ height: '6px' }}>
                                <div
                                    className="progress-bar bg-success rounded-pill animate__animated animate__fadeIn"
                                    role="progressbar"
                                    style={{ width: `${sendingStatus.progress}%`, transition: 'width 0.3s ease' }}
                                    aria-valuenow={sendingStatus.progress}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                            </div>
                        </div>

                        {sendingStatus.currentMemberName && (
                            <div className="text-center small text-muted mt-2">
                                Processando: <strong className="text-dark">{sendingStatus.currentMemberName}</strong>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

TechnicalEvaluationForm.propTypes = {
    classId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    idActivity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    classEmployeeName: PropTypes.string,
    activityName: PropTypes.string,
    evaluationLogic: PropTypes.object
}

export default TechnicalEvaluationForm
